import { FocusScope } from "@react-aria/focus"
import PropTypes from "prop-types"
import { connect } from "react-redux"

import { updateChoice } from "areas/product/store/actions"
import {
  ChoiceRadio,
  OptionTitle,
  OptionPrice,
} from "areas/product/wizard-options/components"
import { isTestMode } from "helpers/application"
import { to_currency } from "helpers/number"
import displayOptionDescription from "helpers/product/display-option-description"
import displayOptionPrice from "helpers/product/display-option-price"

import MetalIcon from "./choice/metal-icon"

const MetalOptions = ({ data, selectedValue, setChoice }) => {
  return (
    <div
      aria-label={`${data.heading.title} Options`}
      className="tablet:px-6 desktop:grid-cols-4 tablet:gap-6 gap-18 grid auto-rows-fr grid-cols-3"
      role="radiogroup"
    >
      {data.options.map((metal) => {
        const selected = selectedValue === metal.value
        return (
          <ChoiceRadio
            checked={selected}
            className="tablet:mb-12 mb-0 flex-col border-2 p-4 pb-8"
            data-cy={`product-wizard-option-sku-${metal.value}`}
            data-is-option="true"
            data-is-selected={selected}
            key={`metal-${metal.value}`}
            onChange={() => setChoice(data.pref, metal)}
            pref={data.pref}
            value={metal.value}
          >
            <MetalIcon as="span" metal={metal} />
            <OptionTitle className="line-clamp-2 text-center">
              {metal.title}
            </OptionTitle>
            <OptionPrice>{to_currency(metal.display_price)}</OptionPrice>
          </ChoiceRadio>
        )
      })}
    </div>
  )
}

MetalOptions.propTypes = {
  data: PropTypes.object,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
}

const InternalMetalSelectionStatic = ({
  data,
  description,
  headingTitle,
  hidePrice,
  price,
  selectedValue,
  setChoice,
}) => {
  if (!data || data.disabled || data?.heading?.disabled) return null

  return (
    <div data-cy={`wizard-accordion-${data.pref}`}>
      <div className="tablet:pl-6 tablet:pr-12 flex justify-between bg-white pt-10">
        <div className="flex h-32 flex-col items-start">
          <h2 className="text-14 tablet:font-bold tablet:tracking-half m-0 font-semibold uppercase text-black">
            Choose Your {headingTitle}
          </h2>
          <p className="text-grey-65 text-12 tablet:font-normal tablet:tracking-half font-semibold">
            {description}
          </p>
        </div>

        {!hidePrice && (
          <p
            className="text-14 tracking-half flex items-center font-bold"
            data-ge-parentproduct-base-price
          >
            {price}
          </p>
        )}
      </div>

      <div className="pb-16 pt-10">
        <FocusScope>
          <MetalOptions
            data={data}
            selectedValue={selectedValue}
            setChoice={setChoice}
          />
        </FocusScope>
      </div>
    </div>
  )
}

InternalMetalSelectionStatic.propTypes = {
  data: PropTypes.object,
  description: PropTypes.string,
  headingTitle: PropTypes.string,
  hidePrice: PropTypes.bool,
  price: PropTypes.string,
  selectedValue: PropTypes.string,
  setChoice: PropTypes.func,
}

const mapStateToProps = (state, ownProps) => {
  const pref = ownProps.pref
  const data = state.wizard.data[pref]
  const selected = state.choices[pref]

  const headingTitle = data?.heading?.title || ""

  return {
    data,
    description: displayOptionDescription(
      data,
      pref,
      state.choices,
      state.diamondSelector,
      state.diamondUI
    ),
    headingTitle,
    price: displayOptionPrice(state, pref),
    selectedValue: selected?.value || "",
  }
}

const mapDispatchToProps = (dispatch) => ({
  setChoice: (pref, metal) => {
    if (!isTestMode) {
      dataLayer.push({
        event: "selected-metal-option",
      })
    }
    dispatch(updateChoice(pref, metal, true))
  },
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalMetalSelectionStatic)
